// Browser-safe.
import { INDIA_LOCALE, IST_TIMEZONE } from '../config.ts';

export const MINUTE_MS = 60_000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

/** A year of an Indian venue's sessions, near enough: 52 weeks less the holidays. */
export const YEAR_SESSIONS = 250;

type Instant = Date | string | number;

/**
 * IST has no daylight saving, so a fixed offset is exact. It is a whole number of quarter hours,
 * so a five- or fifteen-minute bucket counted from the epoch is the exchange's own candle.
 */
const IST_OFFSET = '+05:30';

export const istAt = (day: string, clock: string): Date => new Date(`${day}T${clock}${IST_OFFSET}`);

export const istMidnight = (day: string): Date => istAt(day, '00:00');

/** China keeps one offset all year, as India does. */
export const beijingAt = (day: string, clock: string): Date => new Date(`${day}T${clock}+08:00`);

/** An IST `yyyy-mm-dd` as an instant that formats as that same day in UTC, and in IST. */
export const noonOf = (day: string): Date => new Date(`${day}T12:00:00Z`);

const isoDay = new Intl.DateTimeFormat('en-CA', { timeZone: IST_TIMEZONE });

export const todayInIst = (now: Date): string => isoDay.format(now);

export const istDayTurningAt = (hour: number, now: Date): string =>
  todayInIst(new Date(now.getTime() - hour * HOUR_MS));

function istMinuteOfDay(at: Date): number {
  const [hours = 0, minutes = 0] = istClock(at).split(':').map(Number);
  return hours * 60 + minutes;
}

export const istHour = (now: Date): number => Math.floor(istMinuteOfDay(now) / 60);

export function nextIstAt(from: Date, clock: string): Date {
  const day = todayInIst(from);
  const at = istAt(day, clock);
  return at > from ? at : istAt(addDays(day, 1), clock);
}

export function lastIstAt(now: Date, clock: string): Date {
  const day = todayInIst(now);
  const at = istAt(day, clock);
  return at <= now ? at : istAt(addDays(day, -1), clock);
}

export const weekdayOf = (day: string): number => noonOf(day).getUTCDay();

/** The Monday of the week `day` is in, so a Sunday still reads the week just gone. */
export const mondayOf = (day: string): string => addDays(day, -((weekdayOf(day) + 6) % 7));

export const yearOf = (day: string): number => Number(day.slice(0, 4));

export function addDays(day: string, days: number): string {
  const at = new Date(`${day}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

export const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS);

/** Clamped at zero: a day after `today` is clock skew, since postdating is refused. */
export const daysSince = (day: string, today: string): number =>
  Math.max(0, daysBetween(day, today));

const two = (n: number): string => String(n).padStart(2, '0');

/** The 28th and four days more is always in the month after. */
export const monthEnd = (month: string): string =>
  addDays(`${addDays(`${month}-28`, 4).slice(0, 7)}-01`, -1);

export const isoDayOf = (year: number, month: number, date: number): string =>
  `${String(year).padStart(4, '0')}-${two(month)}-${two(date)}`;

export function parseDay(raw: string): string | null {
  const parts = raw.split(/[/-]/);
  if (parts.length !== 3 || parts.some((p) => !/^\d+$/.test(p))) return null;

  const [a, b, c] = parts as [string, string, string];
  const yearFirst = a.length === 4;
  const [y, m, d] = yearFirst ? [a, b, c] : [c, b, a];
  const year = y.length === 2 ? 2000 + Number(y) : Number(y);
  const month = Number(m);
  const day = Number(d);
  if (y.length !== 4 && y.length !== 2) return null;

  const iso = isoDayOf(year, month, day);
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== iso) return null;
  return iso;
}

const atIst = (options: Intl.DateTimeFormatOptions) => {
  const format = new Intl.DateTimeFormat(INDIA_LOCALE, { ...options, timeZone: IST_TIMEZONE });
  return (at: Instant): string => format.format(new Date(at));
};

const onDay = (options: Intl.DateTimeFormatOptions) => {
  const format = new Intl.DateTimeFormat(INDIA_LOCALE, { ...options, timeZone: 'UTC' });
  return (day: string): string => format.format(noonOf(day));
};

const clock = new Intl.DateTimeFormat('en-GB', {
  timeZone: IST_TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Sorts as the time of day it is. */
export const istClock = (at: Instant): string => clock.format(new Date(at));

export const istStamp = atIst({
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export const istDayMonth = atIst({ day: 'numeric', month: 'short' });

export const istWeekday = atIst({ weekday: 'short' });

export const weekdaySaid = onDay({ weekday: 'short', day: 'numeric', month: 'short' });

export const weekdayYearSaid = onDay({
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

export const weekdayLongSaid = onDay({ weekday: 'long' });

export const daySaid = onDay({ day: 'numeric', month: 'short', year: 'numeric' });

export const monthSaid = onDay({ month: 'long' });

/** "14 Sept". */
export const dayMonthSaid = onDay({ day: 'numeric', month: 'short' });

/** "Sept 2025". */
export const monthYearSaid = onDay({ month: 'short', year: 'numeric' });

export function spanSaid(from: Instant, to: Instant): string {
  const ms = Math.max(0, new Date(to).getTime() - new Date(from).getTime());
  if (ms < MINUTE_MS) return `${Math.floor(ms / 1000)}s`;
  const minutes = Math.floor(ms / MINUTE_MS);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const rest = minutes % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return rest > 0 ? `${hours}h ${rest}m` : `${hours}h`;
  return `${minutes}m`;
}

/** `h23`: this locale otherwise writes midnight as 24, putting it on the wrong day. */
const newYork = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function inNewYork(at: Date): { weekday: number; minutes: number } {
  const parts = newYork.formatToParts(at);
  const read = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';
  return {
    weekday: WEEKDAYS.indexOf(read('weekday')),
    minutes: Number(read('hour')) * 60 + Number(read('minute')),
  };
}

const newYorkDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' });

/** The New York calendar day an instant falls on, as `YYYY-MM-DD`. */
export const dayInNewYork = (at: Date): string => newYorkDate.format(at);

export function newYorkAt(day: string, minutes: number): Date {
  const wall = Date.parse(`${day}T00:00:00Z`) + minutes * MINUTE_MS;
  const daylight = new Date(wall + 4 * HOUR_MS);
  return inNewYork(daylight).minutes === minutes ? daylight : new Date(wall + 5 * HOUR_MS);
}
